import { axiosTassoQuintoApiInstance } from '../tasso-quinto';
import {
  AcquisitionContact,
  AcquisitionOperation,
  AcquisitionYesNo,
} from './acquisitionOperation';

const PRESTITO_LEADS_PATH = '/v1/backoffice/prestiti/leads';

export type PrestitoEmploymentContract =
  | 'Dipendente tempo indeterminato'
  | 'Dipendente tempo determinato'
  | 'Pensionato'
  | 'Lavoratore Autonomo/P.iva';

export type PrestitoTenureBand = '1 anno o meno' | '2 anni' | '3 anni o più';

export type PrestitoLoanPurpose =
  | 'Ristrutturazione'
  | 'Auto'
  | 'Consolidamento debiti'
  | 'Spese Mediche'
  | 'Altro';

export type PrestitoEmployeesNumber = 'Meno di 15 dip' | 'Più di 15 dip';

/**
 * The flat Prestito acquisition command, exactly as the closed backend schema
 * accepts it. A key is present when the customer's branch requires it and
 * absent otherwise: an inactive branch's value is a `400 invalid_request`,
 * never something the backend silently drops. Everything the public funnel
 * adds around this — consent, attribution, consultant and agency routing,
 * tracking, FIDO, `force`, the acquisition origin, the actor — is server-owned
 * or excluded, so it has no place in this type.
 */
export interface CreatePrestitoLeadCommand extends AcquisitionContact {
  /** Whole euros, €2.000–€75.000. */
  loanAmount: number;
  employmentContract: PrestitoEmploymentContract;
  /** Self-employed only. */
  yearsTaxNumber?: PrestitoTenureBand;
  /** Self-employed with a tenure of one year or less only. */
  hasGuarantor?: AcquisitionYesNo;
  /** Whole euros, €100–€500.000; everyone but a pensioner. */
  monthlyIncome?: number;
  /** Whole euros, €100–€500.000; pensioners only. */
  monthlyIncomePensioner?: number;
  /** Permanent and fixed-term employees only. */
  yearsCurrentCompany?: PrestitoTenureBand;
  loanPurpose: PrestitoLoanPurpose;
  hasOtherFinancing: AcquisitionYesNo;
  /** Only when other financing exists. */
  hasLatePreviousLoan?: AcquisitionYesNo;
  /** Permanent employee with a late payment only. */
  employeesNumber?: PrestitoEmployeesNumber;
}

/**
 * Starts the assisted Prestito acquisition: Tasso Quinto owns the whole
 * workflow — product lead, HubSpot Deal, Area Clienti account and credential
 * email — behind this one authenticated command. The browser never reproduces
 * the public funnel's unauthenticated save, close and user-creation calls.
 *
 * The key names the compilation, not the customer: replaying it resumes or
 * returns the same operation, while reusing it for a different command is a
 * conflict. It must therefore stay stable for as long as the operator is
 * compiling one lead.
 */
export const createPrestitoLead = async (
  command: CreatePrestitoLeadCommand,
  idempotencyKey: string
): Promise<AcquisitionOperation> => {
  const { data } = await axiosTassoQuintoApiInstance.post<AcquisitionOperation>(
    PRESTITO_LEADS_PATH,
    command,
    { headers: { 'Idempotency-Key': idempotencyKey } }
  );
  return data;
};
